import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { isNil } from '@gfe/core/utils/shared-utils';

import {
  EntityModificationAction,
  EntityModificationOption
} from '@core/models/entities/entity-visualization-interface';
import { selectTimelineModificationDescriptors } from '@core/store/data-visualization/data-visualization.selectors';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { selectFeatureFlagsState } from '@core/store/feature-flags/feature-flags.selectors';

import {
  TimelineModificationActionDescriptor,
  TimelineModificationDescriptors
} from '@features/components/visualization/timeline/models/timeline-modification-descriptor';

@Injectable({ providedIn: 'root' })
export class TimelineModificationActionsService {
  private readonly store = inject(Store);

  private readonly featureFlagsState = this.store.selectSignal(selectFeatureFlagsState);
  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly descriptors = this.store.selectSignal(
    selectTimelineModificationDescriptors
  );

  private actionsMap = new Map<string, EntityModificationAction[]>();

  setActions(map: Map<string, EntityModificationAction[]>): void {
    this.actionsMap = map;
  }

  getActionsForRow(uid: string): EntityModificationAction[] {
    const active = this.activeFeatures();

    return (this.actionsMap.get(uid) ?? [])
      .filter(
        (action) => isNil(action.requiredFeature) || active.includes(action.requiredFeature)
      )
      .map((action) => this.gateDependents(action, active));
  }

  buildDescriptors(): TimelineModificationDescriptors {
    const descriptors: TimelineModificationDescriptors = {};

    for (const [rowUid, actions] of this.actionsMap) {
      descriptors[rowUid] = actions.map((action) => ({
        actionId: action.labelKey,
        labelKey: action.labelKey,
        icon: action.icon,
        type: action.type,
        targetEntityType: action.targetEntityType,
        currentValue: action.currentValue,
        disabled: action.disabled,
        disabledReasonKey: action.disabledReasonKey,
        requiredFeature: action.requiredFeature,
        options: action.options.map((option) => ({
          optionId: option.labelKey,
          labelKey: option.labelKey,
          icon: option.icon,
          isCurrentValue: option.isCurrentValue,
          disabled: option.disabled,
          disabledReasonKey: option.disabledReasonKey
        }))
      }));
    }

    return descriptors;
  }

  getDescriptorsForRow(uid: string): TimelineModificationActionDescriptor[] {
    const active = this.activeFeatures();

    return (this.descriptors()[uid] ?? []).filter(
      (descriptor) =>
        isNil(descriptor.requiredFeature) || active.includes(descriptor.requiredFeature)
    );
  }

  resolveAction(rowUid: string, actionId: string): EntityModificationAction | undefined {
    return this.getActionsForRow(rowUid).find((action) => action.labelKey === actionId);
  }

  resolveOption(
    action: EntityModificationAction,
    optionId: string
  ): EntityModificationOption | undefined {
    return action.options.find((option) => option.labelKey === optionId);
  }

  /**
   * The flag list survives a failed reload and keeps naming the dataspace it was loaded for,
   * so a planner who switches dataspaces can otherwise carry the previous one's flags into a
   * dataspace that never opted in. Anything but a confirmed load for the dataspace in use
   * counts as no flags at all.
   */
  private activeFeatures(): string[] {
    const flags = this.featureFlagsState();
    const currentDataSpaceId = this.dataspaceState()?.dataspace?.id;

    if (
      !flags.loaded ||
      isNil(currentDataSpaceId) ||
      flags.dataSpaceId !== currentDataSpaceId
    ) {
      return [];
    }

    return flags.activeFeatures;
  }

  private gateDependents(
    action: EntityModificationAction,
    active: string[]
  ): EntityModificationAction {
    const flag = action.dependentsRequireFeature;
    if (isNil(flag) || isNil(action.cascade?.dependents) || active.includes(flag)) {
      return action;
    }

    const { dependents: _dropped, ...cascade } = action.cascade;
    return { ...action, cascade };
  }
}
